import {handleFormError, handleSubmit} from 'app/client/lib/formUtils';
import {buildURL} from 'app/client/lib/urlUtils';
import {LoginAPI} from 'app/client/ui/LoginAPI';
import {textButton} from 'app/client/ui2018/buttons';
import {mediaMedium} from 'app/client/ui2018/cssVars';
import {cssLink} from 'app/client/ui2018/links';
import {loadingSpinner} from 'app/client/ui2018/loaders';
import {textInput} from 'app/client/ui/inputs';
import * as css from 'app/client/ui/LoginPagesCss';
import {buildValidatedPasswordInput, InvalidPasswordError} from 'app/client/ui/ValidatedPasswordInput';
import {dom, DomContents, IDisposableOwner, makeTestId, Observable, styled} from 'grainjs';

const testId = makeTestId('test-sp-');

interface RegistrationFormOptions {
  loginAPI: LoginAPI;
  email: Observable<string>;
  onSuccess: () => void;
}

/**
 * Builds a registration form for a new Grist account. Supports registration with
 * Google, or email and password.
 *
 * On successful submission of the email/password form, `onSuccess` will be called,
 * which advances the sign-up flow to verification (see `buildVerifyEmailForm`).
 *
 * Google Sign-Up redirects to an OAuth flow hosted by Cognito, which further
 * redirects to the main Grist application on success.
 */
export function buildRegistrationForm(owner: IDisposableOwner, options: RegistrationFormOptions) {
  const {loginAPI, email: emailObs, onSuccess} = options;

  const nameObs = Observable.create(owner, '');
  const passwordObs = Observable.create(owner, '');
  const isPasswordValid = Observable.create(owner, false);
  const error = Observable.create(owner, '');
  const pending = Observable.create(owner, false);

  let passwordInput: HTMLElement;

  return dom.frag(
    css.flexJustifyCenter(css.gristLogo()),
    cssRegisterHeading('Start using Grist for free', testId('heading')),
    dom('form',
      css.googleButton(css.googleLogo(), 'Sign up with Google'),
      handleSubmit({
        onSubmit: () => loginAPI.signInWithGoogle(),
      }),
      testId('google-sign-up'),
    ),
    css.loginMethodsSeparator(css.horizontalLine(), css.lightText('or'), css.horizontalLine()),
    dom('form',
      dom.maybe(error, (err) => css.centeredFormError(err, testId('form-error'))),
      cssLabelAndInput(
        css.formLabel('Email', { for: 'email' }),
        textInput(emailObs,
          {
            id: 'email',
            name: 'email',
            autocomplete: 'username',
            placeholder: 'name@host.com',
            type: 'email',
            required: 'true',
          },
          (el) => { setTimeout(() => el.focus(), 10); },
          testId('email-input'),
        ),
      ),
      cssLabelAndInput(
        css.formLabel('Full name', css.lightColor(" (to display to collaborators)"), { for: 'name' }),
        textInput(nameObs,
          {
            id: 'name',
            name: 'name',
            autocomplete: 'name',
            placeholder: 'Enter name',
            type: 'text',
            required: 'true',
          },
          testId('full-name-input'),
        ),
      ),
      cssValidatedPasswordInput(
        css.formLabel('Password', { for: 'new-password' }),
        dom.create(buildValidatedPasswordInput, passwordObs, isPasswordValid,
          (el) => { passwordInput = el; },
          testId('password-input'),
        ),
      ),
      cssSignUpButton('Start using Grist',
        dom.boolAttr('disabled', pending),
        testId('sign-up')
      ),
      css.centeredText(
        'Already have an account? ',
        cssLink('Sign in',
          {href: buildURL('/login').href},
          testId('sign-in'),
        ),
      ),
      handleSubmit({
        pending,
        onSubmit: async ({ email, password, name }) => {
          if (!isPasswordValid.get()) { throw new InvalidPasswordError(); }

          await loginAPI.signUp(email, password, name);
        },
        onSuccess: () => onSuccess(),
        onError: (e) => {
          if (e instanceof InvalidPasswordError) { return passwordInput.focus(); }

          handleFormError(e, error);
        },
      }),
    ),
  );
}

interface VerificationFormOptions {
  loginAPI: LoginAPI;
  email: Observable<string>;
  headingText: string;
  footer: () => DomContents;
}

/**
 * Builds a post-registration form that prompts for a verification code sent via email.
 */
export function buildVerifyEmailForm(owner: IDisposableOwner, options: VerificationFormOptions) {
  const {loginAPI, email, headingText, footer} = options;

  const verificationCode = Observable.create(owner, '');
  const isResendingCode = Observable.create(owner, false);
  const resentCode = Observable.create(owner, false);
  const error = Observable.create(owner, '');
  const pending = Observable.create(owner, false);

  owner.autoDispose(resentCode.addListener(didResend => {
    if (!didResend) { return; }

    error.set('');
    verificationCode.set('');
  }));

  return dom.frag(
    dom.domComputed(isResendingCode, isLoading => {
      if (isLoading) { return css.spinner(loadingSpinner()); }

      return [
        cssVerifyHeading(
          dom.text(use => use(resentCode) ? 'Sent again!' : headingText),
          testId('heading'),
        ),
        dom('form',
          css.formInstructions(
            css.text('We have sent a code by email to ', css.lightlyBolded(email.get()), '.'),
            css.text('Enter it below, or click the link in the email, to confirm your account.'),
            testId('verification-instructions'),
          ),
          dom.maybe(error, (err) => css.formError(err, testId('form-error'))),
          css.formLabel('Verification Code', { for: 'code' }),
          css.codeInput(verificationCode,
            {
              id: 'code',
              name: 'code',
              type: 'text',
              autocomplete: 'off',
              inputmode: 'numeric',
              maxlength: '6',
              required: 'true',
              pattern: '\\d{6}',
            },
            (el) => { setTimeout(() => el.focus(), 10); },
            testId('code-input'),
          ),
          cssResendCode(
            "Didn't receive a code? ",
            css.textButton(
              textButton.cls('-hover-bg-padding-sm'),
              'Resend it',
              {type: 'button'},
              dom.on('click', async () => {
                if (pending.get()) { return; }

                try {
                  isResendingCode.set(true);
                  await loginAPI.confirmSignUp(email.get(), '');
                  resentCode.set(true);
                } catch (e) {
                  handleFormError(e, error);
                } finally {
                  isResendingCode.set(false);
                }
              }),
              dom.boolAttr('disabled', pending),
              testId('resend-code'),
            ),
          ),
          cssConfirmButton('Confirm account',
            dom.boolAttr('disabled', pending),
            testId('confirm')
          ),
          footer(),
          handleSubmit({
            pending,
            onSubmit: ({ code }) => loginAPI.confirmSignUp(email.get(), code),
            onSuccess: () => window.location.assign(buildURL('/verified').href),
            onError: (e) => handleFormError(e, error),
          }),
        ),
      ];
    })
  );
}

const cssHeading = styled('div', `
  font-weight: 500;
  font-size: 32px;
  line-height: 40px;

  @media ${mediaMedium} {
    & {
      font-size: 24px;
      line-height: 32px;
    }
  }
`);

const cssRegisterHeading = styled(cssHeading, `
  text-align: center;
  margin: 16px 0px 24px 0px;
`);

const cssVerifyHeading = styled(cssHeading, `
  margin-bottom: 8px;
`);

const cssLabelAndInput = styled('div', `
  margin-bottom: 20px;
`);

const cssValidatedPasswordInput = styled('div', `
  margin-bottom: 32px;
`);

const cssSignUpButton = styled(css.bigPrimaryButton, `
  width: 100%;
  margin-bottom: 24px;
`);

const cssConfirmButton = styled(css.bigPrimaryButton, `
  margin: 32px 0px 24px 0px;
`);

const cssResendCode = styled(css.text, `
  margin-top: 16px;
`);
